import { ILCHttpResponse } from '@logicalcommander/types'
import { createApi } from '@reduxjs/toolkit/query/react'
import { v4 as uuidv4 } from 'uuid'
import { configManager } from '../../config/configManager'
import { RootState } from '../redux/store'
import { createBaseQueryWithErrorHandling } from './baseQueryWithErrorHandling'

export interface IApiKey {
  id: string
  name: string
  keyPrefix: string
  permissions: string[]
  expiresAt: string | null
  lastUsedAt: string | null
  isActive: boolean
  createdAt: string
  createdBy: string
  /** From API: full name + identity when user exists */
  createdByDisplay?: string
  key?: string // only present on creation
}

export interface ICreateApiKeyRequest {
  name: string
  permissions: string[]
  expiresAt?: string
}

export const apiKeysApi = createApi({
  reducerPath: 'apiKeysApi',
  baseQuery: createBaseQueryWithErrorHandling(`${configManager.api.GATEWAY_URL}/api-keys`, (headers, { getState }) => {
    headers.set('x-request-id', uuidv4())
    const { token, companyToken } = (getState() as RootState).authReducer
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }
    if (companyToken) {
      headers.set('x-company-token', companyToken)
    }
    return headers
  }),
  tagTypes: ['ApiKey'],
  endpoints: (builder) => ({
    getApiKeys: builder.query<IApiKey[], void>({
      query: () => ({ url: '/', method: 'GET' }),
      providesTags: ['ApiKey'],
      transformResponse: (response: ILCHttpResponse<IApiKey[]>) => response.data as IApiKey[],
    }),
    createApiKey: builder.mutation<IApiKey, ICreateApiKeyRequest>({
      query: (body) => ({ url: '/', method: 'POST', body }),
      invalidatesTags: ['ApiKey'],
      transformResponse: (response: ILCHttpResponse<IApiKey>) => response.data as IApiKey,
    }),
    deleteApiKey: builder.mutation<void, string>({
      query: (id) => ({ url: `/${id}`, method: 'DELETE' }),
      invalidatesTags: ['ApiKey'],
    }),
  }),
})

export const { useGetApiKeysQuery, useCreateApiKeyMutation, useDeleteApiKeyMutation } = apiKeysApi
